import { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { uniAiTranscribe, uniAiChat, uniAiHistory, uniAiClearHistory } from '../lib/api';
import { toast } from 'sonner';

const SESSION_KEY = 'uni_ai_session';

function getSession() {
  let s = localStorage.getItem(SESSION_KEY);
  if (!s) {
    s = 'uni-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem(SESSION_KEY, s);
  }
  return s;
}

export function useUniAI() {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [recording, setRecording] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [muted, setMuted] = useState(false);
  const [conversationMode, setConversationMode] = useState(false);

  const sessionId = useRef(getSession());
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const audioRef = useRef(null);
  const mutedRef = useRef(false);
  const recordingRef = useRef(false);
  const convoRef = useRef(false);
  const audioCtxRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => { mutedRef.current = muted; }, [muted]);
  useEffect(() => { recordingRef.current = recording; }, [recording]);
  useEffect(() => { convoRef.current = conversationMode; }, [conversationMode]);

  const loadHistory = useCallback(async () => {
    try {
      const r = await uniAiHistory(sessionId.current);
      setMessages(r.data || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const stopSpeaking = useCallback(() => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    setSpeaking(false);
  }, []);

  const cleanupVAD = useCallback(() => {
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    if (audioCtxRef.current) { try { audioCtxRef.current.close(); } catch { /* noop */ } audioCtxRef.current = null; }
  }, []);

  // forward-declared holders so callbacks can reference each other safely
  const startRecordingRef = useRef(null);
  const stopRecordingRef = useRef(null);

  // Auto-listen for the next turn in hands-free conversation mode
  const maybeAutoListen = useCallback(() => {
    if (!convoRef.current) return;
    setTimeout(() => {
      if (convoRef.current && !recordingRef.current && !mediaRef.current?.state?.match?.(/recording/)) {
        startRecordingRef.current && startRecordingRef.current();
      }
    }, 450);
  }, []);

  const playAudio = useCallback((b64) => {
    if (!b64 || mutedRef.current) { maybeAutoListen(); return; }
    try {
      const audio = new Audio(`data:audio/mp3;base64,${b64}`);
      audioRef.current = audio;
      setSpeaking(true);
      audio.onended = () => { setSpeaking(false); maybeAutoListen(); };
      audio.play().catch(() => { setSpeaking(false); maybeAutoListen(); });
    } catch { setSpeaking(false); maybeAutoListen(); }
  }, [maybeAutoListen]);

  const sendMessage = useCallback(async (text) => {
    if (!text || !text.trim()) return null;
    setMessages(m => [...m, { role: 'user', content: text }]);
    setProcessing(true);
    try {
      const r = await uniAiChat(text, sessionId.current, !mutedRef.current);
      const d = r.data;
      setMessages(m => [...m, { role: 'assistant', content: d.reply, intent: d.intent, created_task: d.created_task }]);
      if (d.created_task) toast.success(`Task created: ${d.created_task.task_name}`);
      if (d.intent === 'navigate' && d.navigate_to) setTimeout(() => navigate(d.navigate_to), 700);
      // Speak (and, in conversation mode, auto-listen after the reply)
      playAudio(d.audio_base64);
      return d;
    } catch (e) {
      const msg = e.response?.data?.detail || 'Uni AI failed to respond';
      setMessages(m => [...m, { role: 'assistant', content: msg }]);
      toast.error(msg);
      return null;
    } finally {
      setProcessing(false);
    }
  }, [navigate, playAudio]);

  // Voice-activity detection: auto-stop after a short silence (used in conversation mode)
  const setupVAD = useCallback((stream) => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      audioCtxRef.current = ctx;
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      src.connect(analyser);
      const data = new Uint8Array(analyser.fftSize);
      let spoke = false;
      let silenceStart = null;
      const startedAt = Date.now();
      const tick = () => {
        analyser.getByteTimeDomainData(data);
        let sum = 0;
        for (let i = 0; i < data.length; i++) { const v = (data[i] - 128) / 128; sum += v * v; }
        const rms = Math.sqrt(sum / data.length);
        const now = Date.now();
        if (rms > 0.045) { spoke = true; silenceStart = null; }
        else if (spoke) {
          if (!silenceStart) silenceStart = now;
          else if (now - silenceStart > 1600) { cleanupVAD(); stopRecordingRef.current && stopRecordingRef.current(); return; }
        }
        if (now - startedAt > 15000) { cleanupVAD(); stopRecordingRef.current && stopRecordingRef.current(); return; }
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    } catch { /* VAD optional */ }
  }, [cleanupVAD]);

  const startRecording = useCallback(async () => {
    stopSpeaking();
    if (recordingRef.current) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = e => { if (e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        cleanupVAD();
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        if (blob.size < 800) return;
        setProcessing(true);
        try {
          const r = await uniAiTranscribe(blob);
          const transcript = (r.data.transcript || '').trim();
          if (transcript) {
            await sendMessage(transcript);
          } else {
            if (!convoRef.current) toast.info("I didn't catch that — please try again.");
            setProcessing(false);
          }
        } catch {
          toast.error('Could not transcribe audio');
          setProcessing(false);
        }
      };
      mediaRef.current = mr;
      mr.start();
      setRecording(true);
      if (convoRef.current) setupVAD(stream);
    } catch {
      toast.error('Microphone access denied. Please allow mic access.');
    }
  }, [sendMessage, stopSpeaking, cleanupVAD, setupVAD]);

  const stopRecording = useCallback(() => {
    cleanupVAD();
    if (mediaRef.current && recordingRef.current) {
      try { mediaRef.current.stop(); } catch { /* noop */ }
      setRecording(false);
    }
  }, [cleanupVAD]);

  useEffect(() => { startRecordingRef.current = startRecording; }, [startRecording]);
  useEffect(() => { stopRecordingRef.current = stopRecording; }, [stopRecording]);

  const toggleRecording = useCallback(() => {
    if (recordingRef.current) stopRecording(); else startRecording();
  }, [startRecording, stopRecording]);

  const toggleConversationMode = useCallback(() => {
    setConversationMode(prev => {
      const next = !prev;
      convoRef.current = next;
      if (next && !recordingRef.current && !processing) {
        setTimeout(() => startRecordingRef.current && startRecordingRef.current(), 200);
      } else if (!next) {
        stopRecordingRef.current && stopRecordingRef.current();
        stopSpeaking();
      }
      return next;
    });
  }, [processing, stopSpeaking]);

  const clearChat = useCallback(async () => {
    try { await uniAiClearHistory(sessionId.current); setMessages([]); }
    catch { /* ignore */ }
  }, []);

  return {
    messages, recording, processing, speaking, muted, setMuted,
    conversationMode, toggleConversationMode,
    toggleRecording, startRecording, stopRecording, sendMessage, clearChat, stopSpeaking,
  };
}
